import {
  Bell,
  Calendar,
  ChevronDown,
  Grid3X3,
  HelpCircle,
  LayoutDashboard,
  Settings,
  Upload,
  Users,
} from 'lucide-react';
import React, { useContext, useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import { HeaderProjectSelector } from './HeaderProjectSelector';
import { NotificationDropdown } from '../notifications';
import { Notification } from '@/types';
import Logo from '../Logo';
import AllItemsMegaMenu from './AllItemsMegaMenu';
import ProjectSelectionModal from './ProjectSelectionModal';

interface AppHeaderProps {
  className?: string;
}

export const AppHeader: React.FC<AppHeaderProps> = () => {
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);

  const [showNotifications, setShowNotifications] = useState(false);
  const [showAllItems, setShowAllItems] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [pendingModule, setPendingModule] = useState<{ route: string; label: string } | null>(null);

  const allItemsRef = useRef<HTMLDivElement>(null);
  const profileRef = useRef<HTMLDivElement>(null);

  // Sanitize user input to prevent XSS
  const sanitizeUserName = (name: string | undefined) => {
    if (!name) return 'N';
    return name.replace(/<[^>]*>/g, '').substring(0, 50);
  };

  const handleLogout = async () => {
    await authContext?.logout();
    navigate('/login');
  };

  const handleNotificationClick = (notification: Notification) => {
    setShowNotifications(false);

    if (notification.actionUrl) {
      if (notification.actionUrl.startsWith('/')) {
        navigate(notification.actionUrl);
      } else {
        window.open(notification.actionUrl, '_blank');
      }
    }
  };

  // Handle click outside for All Items dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (allItemsRef.current && !allItemsRef.current.contains(event.target as Node)) {
        setShowAllItems(false);
      }
    };

    if (showAllItems) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showAllItems]);

  // Handle click outside for Profile dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
        setShowProfile(false);
      }
    };

    if (showProfile) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showProfile]);

  // Handle keyboard navigation for All Items dropdown
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!showAllItems) return;

      switch (event.key) {
        case 'Escape':
          setShowAllItems(false);
          event.preventDefault();
          break;
        case 'Tab':
          // Allow tab navigation through menu items
          break;
      }
    };

    if (showAllItems) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showAllItems]);

  return (
    <header className="bg-white border-b border-gray-200">
      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* LEFT SECTION - Logo + Navigation */}
          <div className="flex items-center space-x-8">
            {/* BuilderBel Logo */}
            <Logo size="sm" />

            {/* Dashboard */}
            <NavLink
              to="/dashboard"
              className={({ isActive }) =>
                `inline-flex items-center px-1 pt-1 text-sm font-medium transition-colors ${
                  isActive
                    ? 'text-brand-600 border-b-2 border-brand-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`
              }
            >
              <LayoutDashboard className="h-4 w-4 mr-2 text-brand-600" />
              Dashboard
            </NavLink>

            {/* Projects */}
            <HeaderProjectSelector />

            {/* All Items */}
            <div className="relative" ref={allItemsRef}>
              <button
                type="button"
                onClick={() => setShowAllItems(!showAllItems)}
                className="inline-flex items-center px-1 pt-1 text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors"
              >
                <Grid3X3 className="h-4 w-4 mr-2" />
                All Items
                <ChevronDown
                  className={`ml-1 h-4 w-4 transform transition-transform ${showAllItems ? 'rotate-180' : ''}`}
                />
              </button>

              {/* All Items Mega Menu */}
              <AllItemsMegaMenu
                isOpen={showAllItems}
                onClose={() => setShowAllItems(false)}
                onSelectModule={(route, label) => setPendingModule({ route, label })}
              />
            </div>

            {/* Calendar */}
            <NavLink
              to="/dashboard/calendar"
              className={({ isActive }) =>
                `inline-flex items-center px-1 pt-1 text-sm font-medium transition-colors ${
                  isActive
                    ? 'text-brand-600 border-b-2 border-brand-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`
              }
            >
              <Calendar className="h-4 w-4 mr-2" />
              Calendar
            </NavLink>
          </div>

          {/* RIGHT SECTION - Actions */}
          <div className="flex items-center space-x-1">
            {/* Import */}
            <button
              type="button"
              className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-brand-600 bg-brand-50 border border-brand-200 rounded-md hover:bg-brand-100 transition-colors"
            >
              <Upload className="h-4 w-4 mr-1.5" />
              Import
            </button>

            {/* Help */}
            <button
              type="button"
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
              title="Help"
            >
              <HelpCircle className="h-5 w-5" />
            </button>

            {/* Apps Grid */}
            <button
              type="button"
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
              title="Apps"
            >
              <Grid3X3 className="h-5 w-5" />
            </button>

            {/* Settings */}
            <button
              type="button"
              onClick={() => navigate('/dashboard/settings')}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
              title="Settings"
            >
              <Settings className="h-5 w-5" />
            </button>

            {/* People */}
            <button
              type="button"
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
              title="People"
            >
              <Users className="h-5 w-5" />
            </button>

            {/* Notifications */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowNotifications(!showNotifications)}
                className="relative p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                title="Notifications"
              >
                <Bell className="h-5 w-5" />
                {/* Notification badge */}
                <span className="absolute top-1 right-1 block h-2 w-2 rounded-full bg-green-500"></span>
              </button>
              <NotificationDropdown
                isOpen={showNotifications}
                onClose={() => setShowNotifications(false)}
                onNotificationClick={handleNotificationClick}
              />
            </div>

            {/* Profile */}
            <div className="relative" ref={profileRef}>
              <button
                type="button"
                onClick={() => setShowProfile(!showProfile)}
                className="flex items-center space-x-1 p-1.5 rounded-full hover:bg-gray-100 transition-colors"
              >
                <div className="w-8 h-8 bg-brand-600 rounded-full flex items-center justify-center text-white font-medium text-sm">
                  {sanitizeUserName(authContext?.user?.firstName)?.[0] || 'N'}
                </div>
              </button>

              {/* Profile Dropdown */}
              {showProfile && (
                <div className="absolute top-full right-0 mt-2 w-64 bg-white rounded-xl shadow-lg border border-gray-200 z-50 py-2">
                  {/* Company Name */}
                  <div className="px-4 py-3 border-b border-gray-200">
                    <p className="text-sm font-medium text-gray-900">
                      {sanitizeUserName(authContext?.user?.companyName) || 'BuilderBel'}
                    </p>
                  </div>

                  {/* User Info */}
                  <div className="px-4 py-3 border-b border-gray-200">
                    <p className="text-sm font-medium text-gray-900">
                      {sanitizeUserName(authContext?.user?.firstName)}{' '}
                      {sanitizeUserName(authContext?.user?.lastName)}
                    </p>
                    <p className="text-sm text-gray-500">
                      {sanitizeUserName(authContext?.user?.email)}
                    </p>
                  </div>

                  {/* Menu Items */}
                  <div className="py-2">
                    <a
                      href="/profile"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      My Profile
                    </a>
                    <a
                      href="/profile/preferences"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      My Preferences
                    </a>
                    <a
                      href="/profile/billing"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      Subscriptions & Billings
                    </a>
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      Logout
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Project Selection Modal - rendered at header level so it survives menu close */}
      {pendingModule && (
        <ProjectSelectionModal
          isOpen={!!pendingModule}
          onClose={() => setPendingModule(null)}
          targetRoute={pendingModule.route}
          moduleLabel={pendingModule.label}
        />
      )}
    </header>
  );
};
